import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bottles, type Bottle } from '@/lib/api';
import { Button, Input, Modal } from '@/design-system';
import { useToasts } from '@/lib/store';

/**
 * Shared "Selg" flow — the manual, session-authed counterpart to the POS
 * sales-feed ingest (routes/pos.ts POST /pos/sales). Used from both the
 * cellar table row actions and BottlePricingDrawer's Prising tab, so the
 * 'sold' kjellerhistorikk activity (see history.activity.sold) gets
 * populated for households that never wire up a POS integration.
 */
export function SellBottleModal({
  open,
  onClose,
  bottle,
}: {
  open: boolean;
  onClose: () => void;
  bottle?: Pick<Bottle, 'id' | 'name' | 'qty' | 'sellingPriceNok'>;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { push } = useToasts();
  const [quantity, setQuantity] = useState(1);
  const [unitPriceNok, setUnitPriceNok] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (open && bottle) {
      setQuantity(Math.min(1, bottle.qty) || 1);
      setUnitPriceNok(bottle.sellingPriceNok);
    }
  }, [open, bottle]);

  const sell = useMutation({
    mutationFn: () => {
      if (!bottle) throw new Error('Ingen flaske valgt');
      return Bottles.sell(bottle.id, { quantity, unitPriceNok });
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['cellar.bottles'] });
      void qc.invalidateQueries({ queryKey: ['cellar.stats'] });
      void qc.invalidateQueries({ queryKey: ['bottle', bottle?.id] });
      void qc.invalidateQueries({ queryKey: ['bottle.events', bottle?.id] });
      void qc.invalidateQueries({ queryKey: ['cellar-events'] });
      void qc.invalidateQueries({ queryKey: ['market-value.cellar'] });
      push({
        title: t('pricing.sell.success'),
        description:
          result.appliedQuantity < result.requestedQuantity
            ? t('pricing.sell.partial', { applied: result.appliedQuantity, requested: result.requestedQuantity })
            : bottle?.name,
        tone: result.appliedQuantity < result.requestedQuantity ? 'warning' : 'success',
      });
      onClose();
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  if (!bottle) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${t('pricing.sell.title')} — ${bottle.name}`}
      footer={
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            iconLeft="check"
            loading={sell.isPending}
            disabled={quantity < 1}
            onClick={() => sell.mutate()}
          >
            {t('pricing.sell.confirm')}
          </Button>
        </div>
      }
    >
      <div className="grid gap-3">
        <Input
          label={t('pricing.sell.quantity')}
          type="number"
          min={1}
          max={Math.max(bottle.qty, 1)}
          value={quantity}
          onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
        />
        <Input
          label={t('pricing.sell.unitPrice')}
          type="number"
          min={0}
          step={5}
          value={unitPriceNok ?? ''}
          onChange={(e) => setUnitPriceNok(e.target.value === '' ? undefined : Number(e.target.value))}
        />
        <p className="text-xs text-mist">{bottle.qty} på lager</p>
      </div>
    </Modal>
  );
}
